import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Annotation } from "@/lib/indexedDB";
import { Trash2, Edit3 } from "lucide-react";

interface AnnotationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAnnotationStart: (config: AnnotationConfig) => void;
  annotations: Annotation[];
  currentPage: number;
  onAnnotationDelete: (id: string) => void;
  onAnnotationUpdate: (id: string, note: string) => void;
  onAnnotationNavigate: (pageNumber: number) => void;
}

export interface AnnotationConfig {
  type: "highlight";
  color: "red" | "yellow" | "green" | "blue";
  opacity: 100 | 30;
}

const colorMap = {
  red: "bg-red-500",
  yellow: "bg-yellow-400",
  green: "bg-green-500",
  blue: "bg-blue-500",
};

export const AnnotationPanel = ({
  isOpen,
  onClose,
  onAnnotationStart,
  annotations,
  currentPage,
  onAnnotationDelete,
  onAnnotationUpdate,
  onAnnotationNavigate,
}: AnnotationPanelProps) => {
  const [color, setColor] = useState<AnnotationConfig["color"]>("yellow");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState("");

  const handleStart = () => {
    onAnnotationStart({ type: "highlight", color, opacity: 30 });
    onClose();
  };

  const handleEdit = (annotation: Annotation) => {
    setEditingId(annotation.id);
    setEditingNote(annotation.note || "");
  };

  const handleSave = (id: string) => {
    onAnnotationUpdate(id, editingNote);
    setEditingId(null);
  };

  const sortedAnnotations = [...annotations].sort((a, b) => a.pageNumber - b.pageNumber);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>讀者標記</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {/* Color Selection */}
          <div className="space-y-3">
            <Label>選擇顏色</Label>
            <div className="grid grid-cols-4 gap-2">
              {(["red", "yellow", "green", "blue"] as const).map((c) => (
                <button
                  key={c}
                  className={`h-10 rounded-md border-2 ${colorMap[c]} ${
                    color === c ? "border-foreground ring-2 ring-foreground" : "border-border"
                  }`}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <Button onClick={handleStart} className="w-full">
            開始標記
          </Button>

          {/* Annotations List */}
          <div className="space-y-3">
            <Label>我的標記</Label>
            <ScrollArea className="h-[calc(100vh-400px)]">
              <div className="space-y-2 pr-4">
                {sortedAnnotations.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    尚無標記
                  </p>
                ) : (
                  sortedAnnotations.map((annotation) => (
                    <div
                      key={annotation.id}
                      className="rounded-lg border bg-card p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => {
                            onAnnotationNavigate(annotation.pageNumber);
                            onClose();
                          }}
                          className="flex-1 flex items-center gap-2 text-left text-sm hover:text-primary"
                        >
                          <div className={`w-4 h-4 rounded ${colorMap[annotation.color]}`} />
                          <span className="font-medium">第 {annotation.pageNumber} 頁</span>
                        </button>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(annotation)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onAnnotationDelete(annotation.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {editingId === annotation.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingNote}
                            onChange={(e) => setEditingNote(e.target.value)}
                            placeholder="新增備註..."
                            className="text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(annotation.id)}
                              className="flex-1"
                            >
                              儲存
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingId(null)}
                            >
                              取消
                            </Button>
                          </div>
                        </div>
                      ) : (
                        annotation.note && (
                          <p className="text-xs text-muted-foreground">
                            {annotation.note}
                          </p>
                        )
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
